/**
 * `DepartmentAdmin` — pagina admin "Organigramma".
 *
 * Rotta `/admin/org`. Presenta l'organizzazione come un albero a tre livelli:
 * **Business Unit → Team → Persone** (con il relativo ruolo). Da qui l'admin
 * disegna la struttura:
 *  - CRUD delle Business Unit (`/api/departments`);
 *  - CRUD dei Team all'interno di una Business Unit
 *    (`/api/departments/:departmentId/teams`, `/api/teams/:id`);
 *  - visualizzazione delle persone raggruppate per team, dei membri della BU
 *    senza team e delle persone senza BU.
 *
 * L'assegnazione di ruolo/BU/team delle singole persone avviene nella pagina
 * "Utenti" (`/admin/users`): qui l'organigramma è la vista d'insieme e la
 * gestione della struttura (BU e Team).
 *
 * Dati:
 *  - `GET /api/org` → albero completo (`OrgTreeDto`);
 *  - `GET /api/departments` → elenco BU (per i menu/azioni);
 *  - `GET /api/teams` → elenco team.
 *
 * La scrittura è consentita solo ad `admin` (autorizzazione autorevole lato
 * API, 403 per i non‑admin). Le mutazioni invalidano le query interessate.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tree, TreeNode } from 'react-organizational-chart';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type {
  CreateTeamRequest,
  OrgPersonDto,
  OrgTreeDto,
  Role,
  UpdateTeamRequest,
  UpdateUserDepartmentRequest,
  UpdateUserRoleRequest,
  UpdateUserTeamRequest,
  UpsertDepartmentRequest,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextInput,
} from '../components/ui';

const orgKey = ['org'] as const;
const departmentsKey = ['departments'] as const;
const teamsKey = ['teams'] as const;

/** Etichette leggibili dei ruoli (il ruolo `reviewer` è mostrato "Team Leader"). */
const ROLE_LABEL: Record<Role, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  reviewer: 'Team Leader',
  employer: 'Dipendente',
};

/**
 * Ordine di priorità dei ruoli nell'elenco persone: prima chi ha più
 * responsabilità (admin → manager → Team Leader → dipendente). Valori più bassi
 * compaiono per primi.
 */
const ROLE_ORDER: Record<Role, number> = {
  admin: 0,
  manager: 1,
  reviewer: 2,
  employer: 3,
};

/** Ordina le persone per ruolo (ROLE_ORDER) e, a parità, per nome. */
function sortPeople(people: OrgPersonDto[]): OrgPersonDto[] {
  const collator = new Intl.Collator('it', { sensitivity: 'base' });
  return [...people].sort((a, b) => {
    const byRole = ROLE_ORDER[a.role] - ROLE_ORDER[b.role];
    if (byRole !== 0) return byRole;
    return collator.compare(a.displayName || a.email, b.displayName || b.email);
  });
}

/**
 * Applica in modo immutabile un nuovo ruolo a una persona ovunque si trovi
 * nell'albero (team, senza team, senza BU), restituendo un nuovo `OrgTreeDto`.
 * Usato per l'update ottimistico del cambio ruolo dalla chip.
 */
function setMemberRoleInTree(
  tree: OrgTreeDto,
  change: { personId: string; role: Role },
): OrgTreeDto {
  const patch = (p: OrgPersonDto): OrgPersonDto =>
    p.id === change.personId ? { ...p, role: change.role } : p;
  return {
    departments: tree.departments.map((dept) => ({
      ...dept,
      teams: dept.teams.map((t) => ({ ...t, members: t.members.map(patch) })),
      membersWithoutTeam: dept.membersWithoutTeam.map(patch),
    })),
    unassigned: tree.unassigned.map(patch),
  };
}

/**
 * Applica in modo immutabile lo spostamento di una persona verso la BU
 * (`toDepartmentId`) e il team (`teamId`) di destinazione, restituendo un nuovo
 * `OrgTreeDto`. Gestisce intra‑BU, cross‑BU e sorgente senza BU: rimuove la
 * persona da ovunque si trovi (qualsiasi team, "senza team" di qualsiasi BU, o
 * "senza BU") e la reinserisce nella destinazione. Se la persona non si trova,
 * l'albero è restituito invariato.
 */
function moveMemberInTree(
  tree: OrgTreeDto,
  move: { personId: string; toDepartmentId: string | null; teamId: string | null },
): OrgTreeDto {
  // 1. Trova ed estrae la persona dalla posizione corrente.
  let person: OrgPersonDto | undefined;
  for (const dept of tree.departments) {
    for (const t of dept.teams) {
      const found = t.members.find((m) => m.id === move.personId);
      if (found) person = found;
    }
    const loose = dept.membersWithoutTeam.find((m) => m.id === move.personId);
    if (loose) person = loose;
  }
  if (!person) {
    person = tree.unassigned.find((m) => m.id === move.personId);
  }
  if (!person) return tree;

  const moved = person;
  const strip = (list: OrgPersonDto[]) => list.filter((m) => m.id !== moved.id);

  // 2. Rimuove la persona da ogni posizione e la inserisce nella destinazione
  //    (una BU+team, oppure la fascia "senza BU" se `toDepartmentId === null`).
  return {
    departments: tree.departments.map((dept) => {
      const isTarget = dept.id === move.toDepartmentId;
      return {
        ...dept,
        teams: dept.teams.map((t) => {
          const without = strip(t.members);
          if (isTarget && t.id === move.teamId) {
            return { ...t, members: [...without, moved] };
          }
          return { ...t, members: without };
        }),
        membersWithoutTeam:
          isTarget && move.teamId === null
            ? [...strip(dept.membersWithoutTeam), moved]
            : strip(dept.membersWithoutTeam),
      };
    }),
    unassigned:
      move.toDepartmentId === null
        ? [...strip(tree.unassigned), moved]
        : strip(tree.unassigned),
  };
}

/**
 * Dati trasportati dal drag di una persona: id, ruolo, BU e team correnti.
 * Servono a `onDragEnd` per validare lo spostamento (solo intra‑BU) e a
 * calcolare la mutazione.
 */
interface PersonDragData {
  type: 'person';
  personId: string;
  role: Role;
  /** BU corrente della persona, `null` se non assegnata ad alcuna BU. */
  departmentId: string | null;
  teamId: string | null;
}

/**
 * Dati di una zona di rilascio. `departmentId === null` con `teamId === null`
 * rappresenta la zona "Senza business unit" (rimuove BU e team); altrimenti
 * `departmentId` è la BU e `teamId` il team target (`null` = "senza team").
 */
interface TeamDropData {
  type: 'team';
  departmentId: string | null;
  teamId: string | null;
}

/** Ordine dei ruoli assegnabili nel menu della chip (allineato a `Role`). */
const ROLE_OPTIONS: Role[] = ['admin', 'manager', 'reviewer', 'employer'];

/** Voce del menu azioni di un nodo (BU o team). */
interface NodeAction {
  key: string;
  label: string;
  icon: 'plus' | 'edit' | 'trash';
  danger?: boolean;
  onSelect: () => void;
}

/**
 * Menu "tre puntini" (kebab) per le azioni di un nodo BU/team. Sostituisce i
 * pulsanti icona inline: le azioni compaiono in un dropdown. Si chiude su click
 * esterno, su Escape e dopo la selezione di una voce.
 */
function NodeActionsMenu({ label, actions }: { label: string; actions: NodeAction[] }) {
  const [open, setOpen] = useState(false);
  // Coordinate (viewport) dell'angolo in alto a destra del trigger: il pannello
  // è reso in un portal su `document.body`, allineato a destra sotto il bottone.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !containerRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Il pannello è ancorato in coordinate fisse: chiudi su scroll/resize per
    // non lasciarlo "staccato" dal trigger.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open]);

  return (
    <div className="org-menu" ref={containerRef}>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        iconOnly
        className="org-menu__trigger"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <Icon name="more" size={14} />
      </Button>
      {open &&
        pos !== null &&
        createPortal(
          <div
            ref={panelRef}
            className="org-menu__panel"
            role="menu"
            style={{ top: pos.top, right: pos.right }}
          >
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className={`org-menu__item${a.danger ? ' org-menu__item--danger' : ''}`}
                onClick={() => {
                  setOpen(false);
                  a.onSelect();
                }}
              >
                <Icon name={a.icon} size={14} />
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

/**
 * Contenuto visivo della card persona (condiviso da nodo trascinabile e
 * overlay). Se è passato `onChangeRole`, la chip del ruolo è un `<select>`
 * mascherato: tap → cambia ruolo. Senza callback (es. nel DragOverlay) resta
 * una chip statica.
 */
function PersonCard({
  person,
  onChangeRole,
}: {
  person: OrgPersonDto;
  onChangeRole?: (personId: string, role: Role) => void;
}) {
  return (
    <div className={`org-node org-node--person org-node--role-${person.role}`}>
      {onChangeRole ? (
        <span className="org-role-chip org-role-chip--editable">
          <select
            value={person.role}
            aria-label={`Ruolo di ${person.displayName || person.email}`}
            title="Cambia ruolo"
            // Evita che il tap sulla chip inneschi il drag della card.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onChangeRole(person.id, e.target.value as Role)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          <span className="org-role-chip__label">{ROLE_LABEL[person.role]}</span>
        </span>
      ) : (
        <span className="org-role-chip">{ROLE_LABEL[person.role]}</span>
      )}
      <span className="org-node__title">{person.displayName || person.email}</span>
    </div>
  );
}

/**
 * Nodo persona trascinabile (dnd-kit). Trasporta {@link PersonDragData} così che
 * il drop possa spostarlo in un altro team della **stessa** BU.
 */
function PersonNode({
  person,
  departmentId,
  teamId,
  onChangeRole,
}: {
  person: OrgPersonDto;
  departmentId: string | null;
  teamId: string | null;
  onChangeRole: (personId: string, role: Role) => void;
}) {
  const data: PersonDragData = {
    type: 'person',
    personId: person.id,
    role: person.role,
    departmentId,
    teamId,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `person:${person.id}`,
    data,
  });
  return (
    <div
      ref={setNodeRef}
      className={`org-draggable${isDragging ? ' org-draggable--dragging' : ''}`}
      {...listeners}
      {...attributes}
    >
      <PersonCard person={person} onChangeRole={onChangeRole} />
    </div>
  );
}

/**
 * Colonna di persone di un team/BU: impilate una sotto l'altra (non a ventaglio
 * ad albero) ed è una zona di rilascio (dnd-kit) per lo spostamento intra‑BU.
 * Le persone sono ordinate per ruolo e poi per nome.
 */
function PeopleColumn({
  people,
  departmentId,
  teamId,
  activeDrag,
  onChangeRole,
}: {
  people: OrgPersonDto[];
  departmentId: string;
  teamId: string | null;
  activeDrag: PersonDragData | null;
  onChangeRole: (personId: string, role: Role) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `team:${teamId ?? 'none'}:${departmentId}`,
    data: { type: 'team', departmentId, teamId } satisfies TeamDropData,
  });

  // Una zona è un bersaglio valido se si sta trascinando una persona verso una
  // posizione diversa dall'attuale (team o BU diversi). Sono ammessi gli
  // spostamenti intra‑BU, cross‑BU e da "senza BU".
  const isValidTarget =
    activeDrag !== null &&
    (activeDrag.departmentId !== departmentId || activeDrag.teamId !== teamId);

  const classes = [
    'org-members',
    activeDrag ? 'org-members--dropzone' : '',
    isValidTarget ? 'org-members--valid' : '',
    isValidTarget && isOver ? 'org-members--over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={setNodeRef} className={classes}>
      {sortPeople(people).map((p) => (
        <PersonNode
          key={p.id}
          person={p}
          departmentId={departmentId}
          teamId={teamId}
          onChangeRole={onChangeRole}
        />
      ))}
      {people.length === 0 && activeDrag !== null && (
        <p className="org-members__empty">Rilascia qui</p>
      )}
    </div>
  );
}

/**
 * Fascia "Senza business unit": elenca le persone non assegnate ad alcuna BU ed
 * è anche una zona di rilascio per rimuovere la BU (drop → BU e team a `null`).
 * Compare quando ci sono persone senza BU oppure durante il drag di una persona
 * che una BU ce l'ha (così la si può trascinare qui per rimuovergliela).
 */
function UnassignedZone({
  people,
  activeDrag,
  onChangeRole,
}: {
  people: OrgPersonDto[];
  activeDrag: PersonDragData | null;
  onChangeRole: (personId: string, role: Role) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'unassigned',
    data: { type: 'team', departmentId: null, teamId: null } satisfies TeamDropData,
  });

  // Bersaglio valido solo se la persona trascinata ha attualmente una BU.
  const isValidTarget = activeDrag !== null && activeDrag.departmentId !== null;

  // Nasconde la fascia quando è vuota e non c'è un drop valido in corso.
  if (people.length === 0 && !isValidTarget) return null;

  const classes = [
    'orgchart__loose',
    activeDrag ? 'orgchart__loose--dropzone' : '',
    isValidTarget ? 'orgchart__loose--valid' : '',
    isValidTarget && isOver ? 'orgchart__loose--over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={setNodeRef} className={classes}>
      <h3 className="orgchart__loose-title">
        Senza business unit
        <span className="org-node__meta"> · {people.length} pers.</span>
      </h3>
      {people.length === 0 ? (
        <p className="org-members__empty">Rilascia qui per rimuovere la business unit</p>
      ) : (
        <ul className="orgchart__loose-people">
          {sortPeople(people).map((p) => (
            <li key={p.id}>
              <PersonNode
                person={p}
                departmentId={null}
                teamId={null}
                onChangeRole={onChangeRole}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DepartmentAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const orgQuery = useQuery({
    queryKey: orgKey,
    queryFn: () => api.get<OrgTreeDto>('/api/org'),
  });

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: orgKey });
    void queryClient.invalidateQueries({ queryKey: departmentsKey });
    void queryClient.invalidateQueries({ queryKey: teamsKey });
  };

  // --- Stato dei dialoghi -------------------------------------------------
  const [isBuCreateOpen, setIsBuCreateOpen] = useState(false);
  const [buName, setBuName] = useState('');
  const [editingBu, setEditingBu] = useState<{ id: string; name: string } | null>(null);
  // Creazione team dentro una BU: memorizza la BU di destinazione.
  const [teamCreateFor, setTeamCreateFor] = useState<{ id: string; name: string } | null>(null);
  const [teamName, setTeamName] = useState('');
  const [editingTeam, setEditingTeam] = useState<{ id: string; name: string } | null>(null);

  // --- Mutazioni Business Unit --------------------------------------------
  const createBu = useMutation({
    mutationFn: (name: string) => {
      const body: UpsertDepartmentRequest = { name };
      return api.post('/api/departments', body);
    },
    onSuccess: () => {
      setBuName('');
      setIsBuCreateOpen(false);
      invalidateAll();
    },
  });

  const renameBu = useMutation({
    mutationFn: (vars: { id: string; name: string }) => {
      const body: UpsertDepartmentRequest = { name: vars.name };
      return api.put(`/api/departments/${vars.id}`, body);
    },
    onSuccess: () => {
      setEditingBu(null);
      invalidateAll();
    },
  });

  const deleteBu = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/departments/${id}`),
    onSuccess: invalidateAll,
  });

  // --- Mutazioni Team ------------------------------------------------------
  const createTeam = useMutation({
    mutationFn: (vars: { departmentId: string; name: string }) => {
      const body: CreateTeamRequest = { name: vars.name };
      return api.post(`/api/departments/${vars.departmentId}/teams`, body);
    },
    onSuccess: () => {
      setTeamName('');
      setTeamCreateFor(null);
      invalidateAll();
    },
  });

  const renameTeam = useMutation({
    mutationFn: (vars: { id: string; name: string }) => {
      const body: UpdateTeamRequest = { name: vars.name };
      return api.put(`/api/teams/${vars.id}`, body);
    },
    onSuccess: () => {
      setEditingTeam(null);
      invalidateAll();
    },
  });

  const deleteTeam = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/teams/${id}`),
    onSuccess: invalidateAll,
  });

  // --- Spostamento persona tra team/BU (drag & drop) -----------------------
  // Supporta intra‑BU, cross‑BU e sorgente senza BU. Se la BU cambia si fanno
  // due chiamate: prima il department (che lato server azzera il team), poi il
  // team di destinazione. Update ottimistico sull'albero org per un feedback
  // immediato; su errore si ripristina lo snapshot e si rilegge dal server.
  const moveMember = useMutation({
    mutationFn: async (vars: {
      personId: string;
      fromDepartmentId: string | null;
      toDepartmentId: string | null;
      teamId: string | null;
    }) => {
      if (vars.fromDepartmentId !== vars.toDepartmentId) {
        const deptBody: UpdateUserDepartmentRequest = { departmentId: vars.toDepartmentId };
        await api.patch(`/api/users/${vars.personId}/department`, deptBody);
      }
      // Cambiare BU azzera già il team lato server; verso "senza BU" non serve
      // (né avrebbe senso) un'altra chiamata al team.
      if (vars.toDepartmentId !== null) {
        const teamBody: UpdateUserTeamRequest = { teamId: vars.teamId };
        await api.patch(`/api/users/${vars.personId}/team`, teamBody);
      }
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: orgKey });
      const previous = queryClient.getQueryData<OrgTreeDto>(orgKey);
      if (previous) {
        queryClient.setQueryData<OrgTreeDto>(
          orgKey,
          moveMemberInTree(previous, {
            personId: vars.personId,
            toDepartmentId: vars.toDepartmentId,
            teamId: vars.teamId,
          }),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(orgKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: orgKey });
    },
  });

  // --- Cambio ruolo dalla chip della persona (update ottimistico) ----------
  const changeRole = useMutation({
    mutationFn: (vars: { personId: string; role: Role }) => {
      const body: UpdateUserRoleRequest = { role: vars.role };
      return api.patch(`/api/users/${vars.personId}/role`, body);
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: orgKey });
      const previous = queryClient.getQueryData<OrgTreeDto>(orgKey);
      if (previous) {
        queryClient.setQueryData<OrgTreeDto>(orgKey, setMemberRoleInTree(previous, vars));
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(orgKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: orgKey });
      // Il cambio ruolo si riflette anche nella lista utenti admin.
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleChangeRole = (personId: string, role: Role) => {
    changeRole.mutate({ personId, role });
  };

  // Sensore con soglia di attivazione: un piccolo movimento distingue il drag
  // dal click sui pulsanti (rinomina/elimina) dentro i nodi.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  // Dati della persona attualmente trascinata (per overlay + evidenziazione).
  const [activeDrag, setActiveDrag] = useState<PersonDragData | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as PersonDragData | undefined;
    setActiveDrag(data?.type === 'person' ? data : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const person = event.active.data.current as PersonDragData | undefined;
    const target = event.over?.data.current as TeamDropData | undefined;
    setActiveDrag(null);
    if (!person || person.type !== 'person' || !target || target.type !== 'team') {
      return;
    }
    // Nessun movimento se la destinazione coincide con la posizione attuale.
    if (target.departmentId === person.departmentId && target.teamId === person.teamId) {
      return;
    }
    moveMember.mutate({
      personId: person.personId,
      fromDepartmentId: person.departmentId,
      toDepartmentId: target.departmentId,
      teamId: target.teamId,
    });
  };

  const org = orgQuery.data;
  const trimmedBu = buName.trim();
  const trimmedTeam = teamName.trim();
  const trimmedBuEdit = editingBu?.name.trim() ?? '';
  const trimmedTeamEdit = editingTeam?.name.trim() ?? '';

  const anyError =
    createBu.isError ||
    renameBu.isError ||
    deleteBu.isError ||
    createTeam.isError ||
    renameTeam.isError ||
    deleteTeam.isError ||
    moveMember.isError ||
    changeRole.isError;

  // Persona attualmente trascinata, risolta a DTO per il DragOverlay.
  const draggedPerson = useMemo<OrgPersonDto | null>(() => {
    if (activeDrag === null || org === undefined) return null;
    for (const dept of org.departments) {
      for (const t of dept.teams) {
        const p = t.members.find((m) => m.id === activeDrag.personId);
        if (p) return p;
      }
      const p = dept.membersWithoutTeam.find((m) => m.id === activeDrag.personId);
      if (p) return p;
    }
    return org.unassigned.find((m) => m.id === activeDrag.personId) ?? null;
  }, [activeDrag, org]);

  const isEmpty = useMemo(
    () =>
      org !== undefined &&
      org.departments.length === 0 &&
      org.unassigned.length === 0,
    [org],
  );

  return (
    <section className="org-admin" aria-labelledby="org-admin-title">
      <PageHeader
        title="Amministrazione — Organigramma"
        titleId="org-admin-title"
        subtitle="Struttura di business unit, team e persone. Ruoli e assegnazioni si gestiscono in Utenti."
        actions={
          <div className="admin-toolbar">
            <Button type="button" variant="primary" onClick={() => setIsBuCreateOpen(true)}>
              Nuova business unit
            </Button>
          </div>
        }
      />

      {anyError && (
        <Alert tone="error">Impossibile salvare la modifica. Riprova.</Alert>
      )}

      {orgQuery.isLoading ? (
        <Loading>Caricamento organigramma…</Loading>
      ) : orgQuery.isError ? (
        <Alert tone="error">Impossibile caricare l'organigramma.</Alert>
      ) : isEmpty ? (
        <EmptyState role="status">
          Nessuna business unit. Creane una con “Nuova business unit”.
        </EmptyState>
      ) : (
        // Organigramma verticale reso con `react-organizational-chart`: un
        // albero per ciascuna Business Unit (le BU restano affiancate in cima).
        // La libreria disegna i connettori padre→figli; sotto ogni team le
        // persone sono raccolte in un'unica label a colonna (`.org-members`) per
        // impilarle una sotto l'altra invece che a ventaglio.
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDrag(null)}
        >
        <div className="orgchart" role="group" aria-label="Organigramma">
          <div className="orgchart__bus">
            {org?.departments.map((dept) => {
              const peopleCount =
                dept.teams.reduce((n, t) => n + t.members.length, 0) +
                dept.membersWithoutTeam.length;
              // Mostra il ramo "Senza team" se ha membri oppure se è una
              // destinazione valida per la persona attualmente trascinata (così
              // si può trascinare qualcuno fuori dal proprio team).
              const showLoose =
                dept.membersWithoutTeam.length > 0 ||
                (activeDrag !== null &&
                  activeDrag.departmentId === dept.id &&
                  activeDrag.teamId !== null);
              return (
                <Tree
                  key={dept.id}
                  lineWidth="2px"
                  lineColor="var(--color-border-strong)"
                  lineBorderRadius="8px"
                  nodePadding="6px"
                  label={
                    <div className="org-node org-node--bu">
                      <div className="org-node__head">
                        <span className="org-node__title">{dept.name}</span>
                      </div>
                      <div className="org-node__meta">
                        {dept.teams.length} team · {peopleCount} pers.
                      </div>
                      <div className="org-node__actions">
                        <NodeActionsMenu
                          label={`Azioni ${dept.name}`}
                          actions={[
                            {
                              key: 'new-team',
                              label: 'Nuovo team',
                              icon: 'plus',
                              onSelect: () => {
                                setTeamName('');
                                setTeamCreateFor({ id: dept.id, name: dept.name });
                              },
                            },
                            {
                              key: 'rename',
                              label: 'Rinomina',
                              icon: 'edit',
                              onSelect: () =>
                                setEditingBu({ id: dept.id, name: dept.name }),
                            },
                            {
                              key: 'delete',
                              label: 'Elimina',
                              icon: 'trash',
                              danger: true,
                              onSelect: () => {
                                if (
                                  window.confirm(
                                    `Eliminare la business unit “${dept.name}”? I suoi team saranno rimossi e le persone disassociate.`,
                                  )
                                ) {
                                  deleteBu.mutate(dept.id);
                                }
                              },
                            },
                          ]}
                        />
                      </div>
                    </div>
                  }
                >
                  {dept.teams.map((team) => (
                    <TreeNode
                      key={team.id}
                      label={
                        <div className="org-node org-node--team">
                          <div className="org-node__head">
                            <span className="org-node__title">{team.name}</span>
                          </div>
                          <div className="org-node__meta">
                            {team.members.length} pers.
                          </div>
                          <div className="org-node__actions">
                            <NodeActionsMenu
                              label={`Azioni team ${team.name}`}
                              actions={[
                                {
                                  key: 'rename',
                                  label: 'Rinomina',
                                  icon: 'edit',
                                  onSelect: () =>
                                    setEditingTeam({ id: team.id, name: team.name }),
                                },
                                {
                                  key: 'delete',
                                  label: 'Elimina',
                                  icon: 'trash',
                                  danger: true,
                                  onSelect: () => {
                                    if (
                                      window.confirm(
                                        `Eliminare il team “${team.name}”? Le persone resteranno nella business unit, senza team.`,
                                      )
                                    ) {
                                      deleteTeam.mutate(team.id);
                                    }
                                  },
                                },
                              ]}
                            />
                          </div>
                        </div>
                      }
                    >
                      {/* Colonna persone: mostrata quando il team ha membri
                          oppure durante un drag (così un team vuoto diventa una
                          zona di rilascio senza connettori penzolanti a riposo). */}
                      {(team.members.length > 0 || activeDrag !== null) && (
                        <TreeNode
                          label={
                            <PeopleColumn
                              people={team.members}
                              departmentId={dept.id}
                              teamId={team.id}
                              activeDrag={activeDrag}
                              onChangeRole={handleChangeRole}
                            />
                          }
                        />
                      )}
                    </TreeNode>
                  ))}

                  {/* Persone della BU senza team: ramo dedicato allo stesso
                      livello dei team. Compare anche vuoto durante un drag, per
                      poterci trascinare una persona. */}
                  {showLoose && (
                    <TreeNode
                      label={
                        <div className="org-node org-node--loose">
                          <div className="org-node__head">
                            <span className="org-node__title">Senza team</span>
                          </div>
                          <div className="org-node__meta">
                            {dept.membersWithoutTeam.length} pers.
                          </div>
                        </div>
                      }
                    >
                      <TreeNode
                        label={
                          <PeopleColumn
                            people={dept.membersWithoutTeam}
                            departmentId={dept.id}
                            teamId={null}
                            activeDrag={activeDrag}
                            onChangeRole={handleChangeRole}
                          />
                        }
                      />
                    </TreeNode>
                  )}
                </Tree>
              );
            })}
          </div>

          {org && (
            <UnassignedZone
              people={org.unassigned}
              activeDrag={activeDrag}
              onChangeRole={handleChangeRole}
            />
          )}
        </div>

          {/* Anteprima trascinata: segue il cursore durante il drag. */}
          <DragOverlay dropAnimation={null}>
            {draggedPerson ? (
              <div className="org-drag-overlay">
                <PersonCard person={draggedPerson} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Creazione business unit */}
      <Modal
        open={isBuCreateOpen}
        onClose={() => setIsBuCreateOpen(false)}
        title="Nuova business unit"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsBuCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="bu-create-form"
              variant="primary"
              loading={createBu.isPending}
              disabled={trimmedBu.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="bu-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedBu.length > 0) createBu.mutate(trimmedBu);
          }}
        >
          <Field id="bu-new-name" label="Nome business unit">
            <TextInput
              id="bu-new-name"
              value={buName}
              onChange={(e) => setBuName(e.target.value)}
              placeholder="Es. Sviluppo software"
            />
          </Field>
        </form>
      </Modal>

      {/* Rinomina business unit */}
      <Modal
        open={editingBu !== null}
        onClose={() => setEditingBu(null)}
        title="Rinomina business unit"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditingBu(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="bu-edit-form"
              variant="primary"
              loading={renameBu.isPending}
              disabled={trimmedBuEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="bu-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (editingBu && trimmedBuEdit.length > 0) {
              renameBu.mutate({ id: editingBu.id, name: trimmedBuEdit });
            }
          }}
        >
          <Field id="bu-edit-name" label="Nome business unit">
            <TextInput
              id="bu-edit-name"
              value={editingBu?.name ?? ''}
              onChange={(e) =>
                setEditingBu((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
            />
          </Field>
        </form>
      </Modal>

      {/* Creazione team dentro una BU */}
      <Modal
        open={teamCreateFor !== null}
        onClose={() => setTeamCreateFor(null)}
        title={teamCreateFor ? `Nuovo team in ${teamCreateFor.name}` : 'Nuovo team'}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setTeamCreateFor(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="team-create-form"
              variant="primary"
              loading={createTeam.isPending}
              disabled={trimmedTeam.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="team-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (teamCreateFor && trimmedTeam.length > 0) {
              createTeam.mutate({ departmentId: teamCreateFor.id, name: trimmedTeam });
            }
          }}
        >
          <Field id="team-new-name" label="Nome team">
            <TextInput
              id="team-new-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Es. Backend"
            />
          </Field>
        </form>
      </Modal>

      {/* Rinomina team */}
      <Modal
        open={editingTeam !== null}
        onClose={() => setEditingTeam(null)}
        title="Rinomina team"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditingTeam(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="team-edit-form"
              variant="primary"
              loading={renameTeam.isPending}
              disabled={trimmedTeamEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="team-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (editingTeam && trimmedTeamEdit.length > 0) {
              renameTeam.mutate({ id: editingTeam.id, name: trimmedTeamEdit });
            }
          }}
        >
          <Field id="team-edit-name" label="Nome team">
            <TextInput
              id="team-edit-name"
              value={editingTeam?.name ?? ''}
              onChange={(e) =>
                setEditingTeam((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
            />
          </Field>
        </form>
      </Modal>
    </section>
  );
}
